import seedJson from "@/data/seed.json";
import * as mock from "@/mocks/server";
import type {
  AddressSuggestion,
  BranchesResponse,
  CartLine,
  Customer,
  Delivery,
  DeliveryArea,
  DeliveryTracking,
  DineinFloor,
  DineinTableWithOrder,
  RiderLocation,
  ResolvedAddress,
  MenuResponse,
  OperationalRoles,
  Order,
  OrderPayload,
  Rating,
  RatingPayload,
  Staff,
  StaffOrder,
  StoreData,
  StoreMetadata,
} from "@/types";
import type { XReportData } from "./xReport";
import { operationalDayQuery } from "./operationalDay";
import type { GiftCard, Offer, Voucher } from "@/types/wallet";
import {
  activeTenantId,
  apiBaseUrl,
  IS_OFFLINE_MODE,
  USE_MOCK_API,
} from "./config";
import type { OrderType } from "@/constants/enums";

/** The bundled snapshot is plain JSON, so it needs one cast at the boundary. */
const seed = seedJson as unknown as {
  data: StoreData;
  metadata: StoreMetadata;
  baseUrl?: string;
};

/** The mock's HTTP face, used from the browser (see pages/api/mock). */
const MOCK_HTTP_PREFIX = "/api/mock";

const IS_SERVER = typeof window === "undefined";

/**
 * A failed request, carrying whatever the server said.
 *
 * The original's surfaces read `error.response.data` and show it verbatim when
 * it is a string — that is how "Invalid Code" reaches the login snackbar
 * instead of a generic message. `fetch` gives us no such envelope, so the body
 * is captured here and `serverMessage()` below performs the same test.
 */
export class ApiError extends Error {
  status: number;
  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

/**
 * The original's `typeof error.response.data === "string" ? … : fallback`,
 * reproduced exactly — a STRING body is shown to the customer, anything else
 * falls through to the calling screen's own copy.
 *
 * Note what that means against `:4001`, which answers `{ message: "Invalid Code" }`
 * rather than a bare string: the object does not satisfy the test, so the
 * screen's own wording renders — `"Invalid Code!"`, `"We couldn't verify you!"`.
 * That is not a loss. It is the original's own else-branch, and its copy is the
 * copy this reconstruction is required to render verbatim; widening the test to
 * unwrap `{ message }` would replace recovered copy with server copy on every
 * failure.
 */
export const serverMessage = (error: unknown): string | undefined => {
  if (!(error instanceof ApiError)) return undefined;
  if (typeof error.body === "string" && error.body.trim()) return error.body;
  return undefined;
};

const requestAt = async <T>(
  base: string,
  path: string,
  options: RequestInit = {},
): Promise<T> => {
  const response = await fetch(`${base}${path}`, {
    headers: { "Content-Type": "application/json", ...(options.headers ?? {}) },
    ...options,
  });
  if (!response.ok) {
    // Read the body before throwing: a 401 from `/web/auth/verify-otp` carries
    // the only wording the customer should see.
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    throw new ApiError(
      `${options.method ?? "GET"} ${path} failed (${response.status})`,
      response.status,
      body,
    );
  }
  return (await response.json()) as T;
};

/**
 * `apiBaseUrl()` is resolved per call, not once at module load: in the
 * browser it derives the host from `window.location`, so the storefront
 * works over localhost, the LAN IP and the public IP from the same build.
 */
const request = <T>(path: string, options?: RequestInit) =>
  requestAt<T>(apiBaseUrl(), path, options);

export interface ReservationRequest {
  tenant_id: string;
  customer_name: string;
  customer_phone: string;
  customer_email: string;
  reservation_time: string;
  num_of_guests: number;
  special_instructions?: string;
}

/** Persist a customer table request so it appears immediately in staff/POS. */
export const createReservation = (payload: ReservationRequest) =>
  request<Record<string, unknown>>("/reservations", {
    method: "POST",
    body: JSON.stringify({ ...payload, reservation_status: "PENDING" }),
  });

/**
 * A LIVE request that fails must never be indistinguishable from a tenant with
 * nothing to show.
 *
 * The early `USE_MOCK_API` / `IS_OFFLINE_MODE` returns below are correct — in
 * those modes the empty list IS the answer. The `catch` blocks were not: on a
 * live build a rejected certificate, a stopped API or a 500 all returned `[]`
 * and every surface rendered its empty state as if the data were simply
 * absent. (That is exactly what happened when `:4001`'s self-signed cert was
 * re-issued: the menu showed an empty-catalog state and blamed the build.)
 *
 * The rule this file now follows:
 *   - fetchers consumed by a THUNK do not catch at all — the slice already has
 *     a `rejected` handler, so the failure reaches the store as a failure;
 *   - fetchers called straight from a component keep their non-throwing
 *     contract (the component has no error path to fall into) but report here
 *     first, so a failure is never silent — **unless the component DOES have
 *     one**, which is the login pair (`sendCustomerOtp` / `verifyCustomerOtp`):
 *     the verification drawer reproduces the original's `catch`, so both throw
 *     and the message reaches its snackbar;
 *   - the two SSR fetchers keep their bundled-snapshot fallback, because a
 *     dead API must not take the whole page down — both now report.
 */
const reportApiFailure = (path: string, error: unknown): void => {
  // eslint-disable-next-line no-console
  console.error(`[api] ${path} failed:`, error);
};

/**
 * Mock calls made from the browser go over HTTP to `/api/mock/*` rather than
 * into the client bundle's own copy of the module. Without this the in-memory
 * order store would be split in two — orders created in the browser would be
 * invisible to `getServerSideProps`.
 */
const mockCall = <T>(
  direct: () => Promise<T>,
  path: string,
  options?: RequestInit,
): Promise<T> =>
  IS_SERVER ? direct() : requestAt<T>(MOCK_HTTP_PREFIX, path, options);

/**
 * Store settings + trading hours + closure dates.
 * Called from `getServerSideProps` so the first paint already knows the
 * brand colour, cover image and which order types are enabled.
 */
export const fetchStoreData = async (
  tenantId = activeTenantId(),
): Promise<StoreData> => {
  if (USE_MOCK_API)
    return mockCall(mock.getStoreData, `/web/store/${tenantId}`);
  if (IS_OFFLINE_MODE) return seed.data;
  try {
    return await request<StoreData>(`/web/store/${tenantId}`);
  } catch (error) {
    console.error(
      "[api] falling back to the bundled store snapshot:",
      (error as Error).message,
    );
    return seed.data;
  }
};

export const fetchStoreMetadata = async (
  tenantId = activeTenantId(),
): Promise<StoreMetadata> => {
  if (USE_MOCK_API)
    return mockCall(mock.getStoreMetadata, `/web/store/${tenantId}/metadata`);
  if (IS_OFFLINE_MODE) return seed.metadata;
  try {
    return await request<StoreMetadata>(`/web/store/${tenantId}/metadata`);
  } catch (error) {
    reportApiFailure(`/web/store/${tenantId}/metadata`, error);
    return seed.metadata;
  }
};

/**
 * ADDITION — the customer-facing branches of this deployment's brand.
 *
 * `GET /web/branches/:tenantId` is not part of the recovered contract; the
 * original storefront serves one tenant and has no picker (see
 * `src/lib/branch.ts`). The tenant passed in is only used to NAME THE BRAND —
 * the endpoint answers with that tenant's whole brand — so nothing here has to
 * know a brand id.
 *
 * Called from `getServerSideProps`, and deliberately NOT caught: the caller
 * decides what a failure means, and for `resolveBranch` it means "fall through
 * to the env default", i.e. the pre-existing single-branch behaviour. Swallowing
 * it into an empty list here would look identical to a brand with no branches.
 *
 * The mock and offline modes have no branch directory at all, and an empty list
 * is the correct answer there: it suppresses the picker.
 */
export const fetchBranches = async (
  tenantId = activeTenantId(),
): Promise<BranchesResponse> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE) return { brand: null, branches: [] };
  const payload = await request<BranchesResponse>(`/web/branches/${tenantId}`);
  return {
    brand: payload?.brand ?? null,
    branches: Array.isArray(payload?.branches) ? payload.branches : [],
  };
};

export const fetchRatings = async (
  tenantId = activeTenantId(),
): Promise<Rating[]> => {
  if (USE_MOCK_API)
    return mockCall(mock.getRatings, `/web/ratings/${tenantId}`);
  if (!tenantId || IS_OFFLINE_MODE) return [];
  // No catch: `loadRatings.rejected` is what tells the drawer the request
  // failed rather than that this tenant has no reviews.
  return request<Rating[]>(`/web/ratings/${tenantId}`);
};

export const fetchMenu = async ({
  tenantId = activeTenantId(),
  orderType,
}: {
  tenantId?: string;
  orderType?: OrderType;
} = {}): Promise<MenuResponse> => {
  if (USE_MOCK_API) {
    return mockCall(() => mock.getMenu({ orderType }), `/web/menu/${tenantId}`);
  }
  if (IS_OFFLINE_MODE) return { categories: [], items: [] };
  const query = orderType ? `?order_type=${encodeURIComponent(orderType)}` : "";
  return request<MenuResponse>(`/web/menu/${tenantId}${query}`);
};

/**
 * The offer catalogue — whole documents, exactly as the original receives them
 * over its socket. The platform filter and the `sort_id` ordering are applied
 * client-side by `selectWorkableOffers`, so nothing is dropped here.
 *
 * This used to return `{ available, claimed }`, which was invented: the
 * original has ONE list and records claims on the CART, not on the offer.
 */
export const fetchOffers = async (tenantId = activeTenantId()): Promise<Offer[]> => {
  if (USE_MOCK_API) return mockCall(mock.getOffers, `/web/offers/${tenantId}`);
  if (IS_OFFLINE_MODE) return [];
  // An API that predates this change answers `{ available, claimed }`; the
  // guard keeps a stale server from putting a non-iterable into the store. A
  // TRANSPORT failure is not guarded — `loadOffers.rejected` must see it.
  const payload = await request<Offer[]>(`/web/offers/${tenantId}`);
  return Array.isArray(payload) ? payload : [];
};

/** The voucher catalogue. Same provenance as the offers above. */
export const fetchVouchers = async (
  tenantId = activeTenantId(),
): Promise<Voucher[]> => {
  if (USE_MOCK_API) {
    return mockCall(mock.getVouchers, `/web/vouchers/${tenantId}`);
  }
  if (IS_OFFLINE_MODE) return [];
  const payload = await request<Voucher[]>(`/web/vouchers/${tenantId}`);
  return Array.isArray(payload) ? payload : [];
};

/** The gift-card catalogue. */
export const fetchGiftCards = async (
  tenantId = activeTenantId(),
): Promise<GiftCard[]> => {
  if (USE_MOCK_API) {
    return mockCall(mock.getGiftCards, `/web/gift-cards/${tenantId}`);
  }
  if (IS_OFFLINE_MODE) return [];
  const payload = await request<GiftCard[]>(`/web/gift-cards/${tenantId}`);
  return Array.isArray(payload) ? payload : [];
};

/**
 * How many times this customer has already been assigned, or has used, each
 * offer / voucher / gift card. The wallet's only HTTP call of its own.
 *
 * A failure is deliberately NOT swallowed into an empty object: the caller
 * stores `null` on rejection, and `null` means "unknown", which is what
 * suppresses the consumed badge. Returning `{}` would assert zero uses.
 */
export const fetchRedemptionCounts = async (
  brandId: string,
  body: {
    offer_ids: string[];
    voucher_ids: string[];
    gift_card_ids: string[];
    phone_number: string;
  },
): Promise<unknown> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE) return null;
  return request<unknown>(
    `/web/customer-redemptions/count/${encodeURIComponent(brandId)}`,
    { method: "POST", body: JSON.stringify(body) },
  );
};

export const fetchDeliveryAreas = async (
  tenantId = activeTenantId(),
): Promise<DeliveryArea[]> => {
  if (USE_MOCK_API) {
    return mockCall(mock.getDeliveryAreas, `/web/delivery-areas/${tenantId}`);
  }
  if (IS_OFFLINE_MODE) return [];
  try {
    return await request<DeliveryArea[]>(`/web/delivery-areas/${tenantId}`);
  } catch (error) {
    reportApiFailure(`/web/delivery-areas/${tenantId}`, error);
    return [];
  }
};

/**
 * Address search for delivery. This tenant is configured
 * `delivery_areas_search_type: "GOOGLE"`, so the customer types their real
 * address rather than picking a zone off a list; the backend biases suggestions
 * to the store's own coordinates and its country.
 */
export const suggestAddresses = async (
  query: string,
  tenantId = activeTenantId(),
): Promise<AddressSuggestion[]> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE || query.trim().length < 3) return [];
  try {
    return await request<AddressSuggestion[]>(
      `/web/address/suggest/${tenantId}?q=${encodeURIComponent(query)}`,
    );
  } catch (error) {
    reportApiFailure(`/web/address/suggest/${tenantId}`, error);
    return [];
  }
};

/**
 * Geocodes the chosen suggestion and answers the only question that matters:
 * do we deliver there? The point is tested against the tenant's drawn delivery
 * polygons (or its radius, when radius delivery is on) and the fee + real road
 * distance come back with it.
 */
export const resolveAddress = async (
  params: { placeId?: string; lat?: number; lng?: number; orderValue?: number },
  tenantId = activeTenantId(),
): Promise<ResolvedAddress | null> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE) return null;
  const query = new URLSearchParams();
  if (params.placeId) query.set("place_id", params.placeId);
  if (params.lat != null) query.set("lat", String(params.lat));
  if (params.lng != null) query.set("lng", String(params.lng));
  if (params.orderValue != null) query.set("order_value", String(params.orderValue));
  try {
    return await request<ResolvedAddress>(
      `/web/address/resolve/${tenantId}?${query.toString()}`,
    );
  } catch (error) {
    reportApiFailure(`/web/address/resolve/${tenantId}`, error);
    return null;
  }
};

/**
 * Sends a login OTP to the customer's phone (WhatsApp or SMS).
 *
 * **This one THROWS**, unlike the other component-facing fetchers in this file.
 * The rule at the top is that a fetcher stays non-throwing when its caller has
 * no error path to fall into; the verification drawer has one — the original's
 * own `catch` raises a snackbar reading the server's message, falling back to
 * "We couldn't verify you!" — so swallowing the failure here would silently
 * advance the customer to a code screen for a code that was never sent.
 */
export const sendCustomerOtp = async (
  phoneNumber: string,
  channel: "wa" | "sms" = "wa",
  tenantId = activeTenantId(),
): Promise<boolean> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE) return true;
  const result = await request<{ delivered?: boolean; provider?: string }>("/web/auth/send-otp", {
    method: "POST",
    body: JSON.stringify({ phone_number: phoneNumber, channel, tenant_id: tenantId }),
  });
  if (result.delivered !== true) {
    throw new ApiError(
      "OTP delivery unavailable",
      503,
      channel === "sms"
        ? "SMS could not be sent. Please try again shortly."
        : "WhatsApp code could not be sent. Please try again shortly.",
    );
  }
  return true;
};

/**
 * Verifies the code and returns the customer. Throws on a bad code so the
 * drawer can show "We couldn't verify you!" rather than silently signing
 * someone in.
 */
export const verifyCustomerOtp = async (
  phoneNumber: string,
  otp: string,
  tenantId = activeTenantId(),
): Promise<Customer> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE) {
    return { phone: phoneNumber, verified: true };
  }
  const { customer } = await request<{ customer: Customer }>("/web/auth/verify-otp", {
    method: "POST",
    body: JSON.stringify({ phone_number: phoneNumber, otp, tenant_id: tenantId }),
  });
  return customer;
};

export const verifyFirebaseCustomerOtp = async (
  idToken: string,
  phoneNumber: string,
  tenantId = activeTenantId(),
): Promise<Customer> => {
  const { customer } = await request<{ customer: Customer }>("/web/auth/firebase-session", {
    method: "POST",
    body: JSON.stringify({ id_token: idToken, phone_number: phoneNumber, tenant_id: tenantId }),
  });
  return customer;
};

export const logFirebaseCustomerOtp = async (payload: {
  phone_number: string;
  status: "sent" | "failed";
  error?: string | null;
}): Promise<void> => {
  try {
    await request("/otp-logs/event", {
      method: "POST",
      body: JSON.stringify({
        ...payload,
        domain: typeof window === "undefined" ? null : window.location.hostname,
      }),
    });
  } catch {
    // Logging must never block authentication.
  }
};

/**
 * The dine-in LAYOUT — floors, each carrying its own tables.
 *
 * The original never fetches tables on their own: `dineinLayout` is one key of
 * its single bundled `GET /staff-ordering/data/:host?keys=…` call, and only
 * `/menu/dine-in` asks for it. This tree's per-resource REST equivalent is
 * `/web/tables/:tenantId`, which serves the same floor-shaped payload — and
 * serves it from the tables EMBEDDED in `dinein_floors`, which are the ones the
 * POS floor plan is keyed on.
 *
 * No catch: `loadDineinLayout.rejected` is what tells the picker the request
 * failed, rather than that this tenant has no floors.
 */
export const fetchDineinLayout = async (
  tenantId = activeTenantId(),
): Promise<DineinFloor[]> => {
  if (USE_MOCK_API)
    return mockCall(mock.getDineinLayout, `/web/tables/${tenantId}`);
  if (IS_OFFLINE_MODE) return [];
  return request<DineinFloor[]>(`/web/tables/${tenantId}`);
};

/**
 * `GET /dinein-tables/:tenant/:table?with_order=true` — THE TABLE-RESUME
 * LOOKUP, recovered from `order/fetchDineinOrderDetails`:
 *
 * ```js
 * let { data: { order: e, ...t } } = await a.Q.get(
 *   "/dinein-tables/".concat(encodeURIComponent(s._id), "/")
 *     .concat(encodeURIComponent(r), "?with_order=true"));
 * ```
 *
 * It asks the one question the storefront previously could not: does this table
 * already have a bill running? `order` is `null` when it does not. The
 * eligibility rule — settled, cancelled, completed and deleted bills are never
 * returned — lives on the server, exactly as it does in the original, whose
 * client has no fallback predicate either.
 *
 * It answers `null` on failure rather than throwing, and the CALLER treats that
 * as "could not resume" rather than "table is free" — the difference between
 * the two is a duplicate bill.
 */
export const fetchDineinTableWithOrder = async (
  tableId: string,
  tenantId = activeTenantId(),
): Promise<DineinTableWithOrder | null> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE) return null;
  try {
    return await request<DineinTableWithOrder>(
      `/web/dinein-tables/${encodeURIComponent(tenantId)}/${encodeURIComponent(tableId)}?with_order=true`,
    );
  } catch (error) {
    reportApiFailure(`/web/dinein-tables/${tenantId}/${tableId}`, error);
    return null;
  }
};

/**
 * `PUT /dinein-tables/:tenantId/:tableId` — START or END a sitting.
 *
 * The POS's own `startEndDineinSitting` (`pos-recovered
 * src/features/dinein/dineinThunks.js:114-188`), same endpoint, same body. It
 * is NOT a `/web/*` route and that is deliberate: this is a till action, not a
 * customer one, and duplicating it under `/web/*` would be a second
 * implementation of a write that the POS, the admin and now the staff floor
 * plan all have to agree about. The storefront already calls two other
 * non-`/web/` routes the same way (`/tenant-staff/…`, `/orders/reports/…`).
 *
 * `num_of_guests: 0` IS the end-of-sitting: `:4001` nulls `order_id`,
 * `seated_at` and `order_placed_at` when the count is not positive.
 *
 * `?persist=true` keeps the existing clocks — used when re-saving an already
 * seated table (a guest-count correction), so the "seated for" timer is not
 * reset to zero by an edit.
 *
 * The TENANT-SCOPED spelling is the one used here on purpose. `:4001` also
 * serves the tenant-less `PUT /dinein-tables/:tableId`, which matches on the
 * table id across every floor of every tenant on the shared database.
 */
export const startEndDineinSitting = async (
  tableId: string,
  guests: number,
  options: { tenantId?: string; persist?: boolean; orderId?: string | null } = {},
): Promise<Record<string, unknown>> => {
  const tenantId = options.tenantId || activeTenantId();
  const query = options.persist ? "?persist=true" : "";
  return request<Record<string, unknown>>(
    `/dinein-tables/${encodeURIComponent(tenantId)}/${encodeURIComponent(tableId)}${query}`,
    {
      method: "PUT",
      body: JSON.stringify({
        num_of_guests: Math.max(0, Math.floor(Number(guests) || 0)),
        ...(options.orderId !== undefined ? { order_id: options.orderId } : {}),
      }),
    },
  );
};

/**
 * `PUT /dinein-tables/move/:tenantId` — move a sitting AND its bill to another
 * table.
 *
 * Verbatim contract from the POS's `moveDineinTable` (`dineinThunks.js:30-51`,
 * confirmed in the bundle at `main-formatted.js:160398-160420`): the client
 * sends only the two ids and the SERVER performs the swap — it transfers
 * `order_id` / `num_of_guests` / `seated_at` / `order_placed_at` onto the
 * destination, clears the source, and repoints the order's own
 * `dinein_details` at the new table. Both table changes are then broadcast, so
 * every POS board and every other web session sees the move without a refresh.
 *
 * This is the whole of "able to change table" — and note that it moves the BILL
 * with the table, which is why it is one server call and not two client writes.
 */
export const moveDineinTable = async (
  fromTableId: string,
  toTableId: string,
  tenantId = activeTenantId(),
): Promise<Record<string, unknown>> =>
  request<Record<string, unknown>>(
    `/dinein-tables/move/${encodeURIComponent(tenantId)}`,
    {
      method: "PUT",
      body: JSON.stringify({ from_table_id: fromTableId, to_table_id: toTableId }),
    },
  );

/**
 * `PUT /web/orders/:orderId` — add a ROUND to the open bill this cart adopted.
 *
 * The original's `postEditOrder` branches `if (_id)` to a PUT and is only
 * allowed to POST when it holds no adopted order; this is that branch. It sends
 * the NEW round's items only — `:4001` re-derives every figure from the catalog
 * and the tenant's settings, because a public storefront cannot be trusted with
 * a total the way a till on a private network can. Throws, so the caller's own
 * catch raises the snackbar.
 */
export const appendToOrder = async (
  orderId: string,
  payload: OrderPayload,
): Promise<Order> =>
  request<Order>(`/web/orders/${encodeURIComponent(orderId)}`, {
    method: "PUT",
    body: JSON.stringify({ ...payload, items: toWireItems(payload.items) }),
  });

export const fetchRecentOrders = async (
  tenantId = activeTenantId(),
): Promise<Order[]> => {
  if (USE_MOCK_API) {
    return mockCall(mock.getRecentOrders, `/web/orders/recent/${tenantId}`);
  }
  if (IS_OFFLINE_MODE) return [];
  try {
    return await request<Order[]>(`/web/orders/recent/${tenantId}`);
  } catch (error) {
    reportApiFailure(`/web/orders/recent/${tenantId}`, error);
    return [];
  }
};

/**
 * Every order in ONE request, matching the original's `history` slice
 * (`POST /orders/:tenantId/bulk` with `{ order_ids }`). POST rather than GET so
 * the ids never appear in a URL or a server access log.
 */
export const fetchOrdersBulk = async (
  orderIds: string[],
  tenantId = activeTenantId(),
): Promise<Order[]> => {
  if (!orderIds.length) return [];
  if (USE_MOCK_API) {
    const all = await mockCall(mock.getRecentOrders, `/web/orders/recent/${tenantId}`);
    return all.filter((order) => orderIds.includes(order._id));
  }
  if (IS_OFFLINE_MODE) return [];
  return request<Order[]>(`/web/orders/${tenantId}/bulk`, {
    method: "POST",
    body: JSON.stringify({ order_ids: orderIds }),
  });
};

/**
 * Re-reads a persisted session so a returning customer's `order_ids` are
 * current. The original refreshes the customer from Firebase's
 * `onAuthStateChanged`; we have no Firebase session here, so the persisted
 * phone number is the key. POST keeps it out of the URL.
 */
export const refreshCustomerSession = async (
  phoneNumber: string,
  tenantId = activeTenantId(),
): Promise<Customer | null> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE || !phoneNumber) return null;
  try {
    const { customer } = await request<{ customer: Customer }>("/web/auth/session", {
      method: "POST",
      body: JSON.stringify({ phone_number: phoneNumber, tenant_id: tenantId }),
    });
    return customer ?? null;
  } catch (error) {
    reportApiFailure("/web/auth/session", error);
    return null;
  }
};

/* ------------------------------- staff auth ------------------------------- */

/**
 * `POST /app-auth/switch/{tenantId}/{code}` → `{ staff, customToken }`.
 *
 * The original's `switchStaffAuth` (`88745.R` / `Mq`) posts an EMPTY body to
 * this exact URL, signs the returned `customToken` into Firebase and lets the
 * auth listener read the staff record back. There is no Firebase here, so the
 * `customToken` is discarded and the `staff` in the same response is used
 * directly — which is what the original's own reducer does with it anyway
 * (`R.fulfilled → { staff: payload, isStaff: true }`). The token is minted for
 * the POS's Firebase project and buys this deployment nothing.
 *
 * It THROWS on a bad code. The caller's catch raises the `"Please try again!"`
 * snackbar, exactly as the original's does.
 */
export const switchStaffAuth = async (
  code: string,
  tenantId = activeTenantId(),
): Promise<Staff> => {
  const { staff } = await request<{ staff: Staff | null }>(
    `/app-auth/switch/${encodeURIComponent(tenantId)}/${encodeURIComponent(code)}`,
    { method: "POST" },
  );
  if (!staff) {
    throw new ApiError("no staff on the switch response", 404, undefined);
  }
  return staff;
};

/** Resolve a website operator only among stores belonging to the current brand. */
export const resolveBrandStaff = async (
  identity: { phone_number?: string; code?: string },
  tenantId = activeTenantId(),
): Promise<Staff | null> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE || !tenantId) return null;
  try {
    const answer = await request<{ staff: Staff | null; tenant_id?: string }>(
      `/web/staff/resolve/${encodeURIComponent(tenantId)}`,
      { method: "POST", body: JSON.stringify(identity) },
    );
    return answer.staff
      ? { ...answer.staff, tenant_id: answer.tenant_id || answer.staff.tenant_id }
      : null;
  } catch (error) {
    // A customer phone/PIN is expected not to match a staff member. The staff
    // lookup runs before the normal customer branch, so a 404 here is control
    // flow rather than an API failure and must not spam the console on every
    // customer sign-in.
    if (error instanceof ApiError && error.status === 404) return null;
    reportApiFailure(`/web/staff/resolve/${tenantId}`, error);
    return null;
  }
};

/**
 * `GET /tenant-staff/{tenantId}/{uid}` — the original's `fetchStaffDetails`
 * (`88745.T`), used to re-read the operator on every boot.
 *
 * A **disabled** staff member comes back as stored rather than hidden: the
 * client derives `isStaff` from `is_disabled`, so swallowing the record here
 * would turn "your account is disabled" into "you are not staff".
 */
export const fetchStaffDetails = async (
  uid: string,
  tenantId = activeTenantId(),
): Promise<Staff | null> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE || !uid) return null;
  return request<Staff | null>(
    `/tenant-staff/${encodeURIComponent(tenantId)}/${encodeURIComponent(uid)}`,
  );
};

/**
 * Digits only, `+`-prefixed — enough to compare two spellings of one number.
 *
 * Not `getIntNumberFromPhone`: that one needs a country to resolve a LOCAL
 * number, and both sides of every comparison here are already international.
 */
const samePhone = (a?: string | null, b?: string | null): boolean => {
  const digits = (v?: string | null) => String(v ?? "").replace(/\D/g, "");
  const left = digits(a);
  return Boolean(left) && left === digits(b);
};

/**
 * `GET /tenant-staff/phone/{phone}/exists?tenant_id={tenantId}` → a bare boolean.
 *
 * Step 3 of the original's staff/customer fork (`88745.O`, §1.2). The original
 * reaches it only after the Firebase custom claim `is_staff` has already said
 * yes; there are no custom claims in this deployment (`mintCustomToken` sets
 * `phone_number` alone — gap #12 of the spec's coverage table), so this call IS
 * the gate rather than its confirmation. Recorded deviation.
 *
 * It is called WITHOUT `platform_type`, exactly as the original calls it. That
 * matters: a staff member who may use the till but not the storefront is still
 * staff — they simply have no storefront permissions, because the
 * `allowed_platforms` test lives in `hasPermission` and not in this fork.
 *
 * Answers `false` on any failure. A staff lookup that cannot be made is not a
 * staff member, which is the safe direction.
 */
export const staffPhoneExists = async (
  phoneNumber: string,
  tenantId = activeTenantId(),
): Promise<boolean> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE || !phoneNumber) return false;
  try {
    const answer = await request<boolean>(
      `/tenant-staff/phone/${encodeURIComponent(phoneNumber)}/exists?tenant_id=${encodeURIComponent(tenantId)}`,
    );
    return answer === true;
  } catch {
    return false;
  }
};

/**
 * The staff record behind a signed-in phone number.
 *
 * The original does not need this: its `fetchStaffDetails` is handed a Firebase
 * uid by the auth listener and reads `/tenant-staff/{tenantId}/{uid}` straight
 * off it. This deployment has no Firebase session, so the phone number IS the
 * identity, and `POST /app-auth/login/{tenantId}` is the call `:4001` already
 * ships for "this identity signed in — which operator is it?" (the POS's own
 * sign-in, spec row #2).
 *
 * ### The guard is load-bearing — do not remove it
 *
 * `:4001`'s `resolveOperator` FALLS BACK to the tenant's highest-privilege
 * enabled operator when the identity matches nobody. Verified against the live
 * API: posting `+923009999999` (a number belonging to no staff member at all)
 * answers with the tenant's `ADMINSTRATOR`. Trusting that response would hand
 * every signed-in customer an admin's role.
 *
 * So the returned record is only accepted when its own `phone_number` is the
 * number we asked about. A fallback answer names somebody else and is dropped.
 * The `customToken` on the response is for the POS's Firebase project and is
 * discarded, as it is on `switchStaffAuth`.
 */
export const resolveStaffByPhone = async (
  phoneNumber: string,
  tenantId = activeTenantId(),
): Promise<Staff | null> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE || !phoneNumber) return null;
  try {
    const { staff } = await request<{ staff: Staff | null }>(
      `/app-auth/login/${encodeURIComponent(tenantId)}`,
      { method: "POST", body: JSON.stringify({ phone_number: phoneNumber }) },
    );
    if (!staff || !samePhone(staff.phone_number, phoneNumber)) return null;
    return staff;
  } catch (error) {
    reportApiFailure(`/app-auth/login/${tenantId}`, error);
    return null;
  }
};

/**
 * `GET /tenant-staff/{tenantId}?type=order_takers` — the assign-order-taker
 * roster (`63998.d`, `fetchAllStaff`).
 *
 * The client filters it again through its own selector (CASHIER / STAFF /
 * SALES_PERSON, non-disabled) because the original does; `:4001` now honours
 * the `type` parameter as well, which is what keeps colleagues' access codes
 * out of the browser.
 *
 * Answers `[]` on a failure — the modal has a designed empty state and an
 * exception here would take the checkout page down with it.
 */
export const fetchOrderTakers = async (
  tenantId = activeTenantId(),
): Promise<Staff[]> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE) return [];
  try {
    const rows = await request<Staff[]>(
      `/tenant-staff/${encodeURIComponent(tenantId)}?type=order_takers`,
    );
    return Array.isArray(rows) ? rows : [];
  } catch (error) {
    reportApiFailure(`/tenant-staff/${tenantId}?type=order_takers`, error);
    return [];
  }
};

/**
 * `GET /orders/reports/STAFF_ID={uid}/{tenantId}?from_date=…&to_date=…`
 *
 * Shared by both staff drawers (`ni` and `rd`). The report *type* is the
 * literal string `STAFF_ID=<uid>` occupying the `:reportType` path segment —
 * that is the original's own URL, not a query parameter, and the uid inside it
 * is `encodeURIComponent`-ed on its own before the segment is assembled.
 *
 * It THROWS. Both drawers have an error screen ("Failed to load orders") and
 * it is the only thing that raises it.
 */
export const fetchStaffOrders = async (
  uid: string,
  tenantId = activeTenantId(),
): Promise<StaffOrder[]> => {
  const rows = await request<StaffOrder[]>(
    `/orders/reports/STAFF_ID=${encodeURIComponent(uid)}/${encodeURIComponent(tenantId)}${operationalDayQuery()}`,
  );
  return Array.isArray(rows) ? rows : [];
};

/**
 * `GET /analytics/x-report/{tenantId}?from_date=…&to_date=…&order_taker_id={uid}`
 *
 * `51303.c`. The five optional filter parameters (`platforms`, `order_statuses`,
 * `association`, `sale_type`, `time_bias`) come from the POS's `commonFilters`
 * slice, which the storefront does not have — the original only ever appends
 * them when they differ from their defaults, so their absence here is the same
 * URL an unfiltered POS produces.
 *
 * On failure the original's thunk `rejectWithValue`s the EMPTY enum-keyed
 * object rather than nothing, so the drawer renders "No Data" instead of
 * blanking. Reproduced by the slice, so this one throws.
 */
export const fetchXReport = async (
  uid: string,
  tenantId = activeTenantId(),
): Promise<XReportData> =>
  request<XReportData>(
    `/analytics/x-report/${encodeURIComponent(tenantId)}${operationalDayQuery()}&order_taker_id=${encodeURIComponent(uid)}`,
  );

/**
 * `GET /operational-roles/{tenantId}` — the `{ [ROLE]: permission[] }` matrix
 * every permission check reads. The original sends the Firebase bearer and
 * `x-api-key` headers with it; `:4001` scopes by tenant and needs neither.
 *
 * It answers `null` rather than throwing, because a matrix that failed to load
 * must read as "no permissions", never as "signed out".
 */
export const fetchOperationalRoles = async (
  tenantId = activeTenantId(),
): Promise<OperationalRoles | null> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE) return null;
  try {
    return await request<OperationalRoles>(
      `/operational-roles/${encodeURIComponent(tenantId)}`,
    );
  } catch (error) {
    reportApiFailure(`/operational-roles/${tenantId}`, error);
    return null;
  }
};

export const fetchOrder = async (
  orderId: string,
  tenantId = activeTenantId(),
): Promise<Order | null> => {
  if (USE_MOCK_API) {
    return mockCall(() => mock.getOrder(orderId), `/web/orders/${orderId}`);
  }
  if (IS_OFFLINE_MODE) return null;
  return request<Order | null>(
    `/web/orders/${encodeURIComponent(orderId)}?tenant_id=${encodeURIComponent(tenantId)}`,
  );
};

/**
 * The ONE place a basket becomes a wire payload.
 *
 * The option shape is sent as the TRUTH — the option's own unit `price`, the
 * `quantity` chosen and whether the group's free allowance covered it — because
 * `:4001` now honours all three (`priceLines` in mongo-api/src/routes/web.js,
 * mirroring the original's `calculateItemModifiersPrice`). It used to collapse
 * `price × quantity` here because the server multiplied by a hardcoded 1; the
 * pre-flattened figure priced correctly but threw the multiplicity away, so the
 * stored order could not say "×2 extra cheese" and ingredient consumption —
 * which scales an option's recipe by `opt.quantity` — deducted a single portion.
 *
 * ORDERING CONSTRAINT: this shape needs the updated `:4001`. Against the older
 * server an option with `quantity > 1` would be charged once. The two must be
 * deployed together.
 */
const toWireItems = (items: CartLine[]): CartLine[] =>
  items.map((line) => ({
    ...line,
    modifiers: (line.modifiers ?? []).map((modifier) => ({
      _id: modifier._id,
      ref: modifier.ref,
      name: modifier.name,
      price: modifier.price ?? 0,
      quantity: modifier.quantity ?? 1,
      is_free: Boolean(modifier.is_free),
    })),
  }));

/**
 * `GET /orders/:tenantId/:orderId` — the FULL STORED order document.
 *
 * PRODUCT CHANGE §3 (`DEVIATIONS.md`). `/web/orders/:orderId` is deliberately a
 * REDUCED, rescaled view (`toWebOrder` flattens categories into a flat item
 * list and converts cents to major units), which is right for rendering and
 * useless for printing: `POST /__local-print/order` renders an ESC/POS receipt
 * from the STORED shape — `categories[].items[]`, cents, `platform`,
 * `consumer`, `dinein_details`. So the print path reads the raw document, and
 * that route already exists (`mongo-api/src/routes/orders.js`) — no API change
 * was needed for this.
 *
 * It throws; the print flow catches and falls back to the browser dialog.
 */
export const fetchStoredOrder = async (
  orderId: string,
  tenantId = activeTenantId(),
): Promise<Record<string, unknown>> =>
  request<Record<string, unknown>>(
    `/orders/${encodeURIComponent(tenantId)}/${encodeURIComponent(orderId)}`,
  );

/** What `GET /__local-print/capabilities` answers. */
export interface LocalPrintCapabilities {
  /** The `payload.units` declarations the bridge honours, e.g. `["major","cents"]`. */
  units?: string[];
}

/**
 * Ask the print bridge what it understands, BEFORE sending it anything.
 *
 * This exists because of the one failure a print path cannot take back: by the
 * time a response says "I ignored your units", the wrong bill is already on
 * paper and in a customer's hand. A deployment that predates the units contract
 * answers 404 here, and the caller then declines the thermal path rather than
 * printing figures that are 100× over.
 *
 * Never throws — an unreachable or older bridge is reported as "no declared
 * capabilities", which is the same conservative answer.
 */
export const fetchLocalPrintCapabilities =
  async (): Promise<LocalPrintCapabilities | null> => {
    try {
      return await request<LocalPrintCapabilities>("/__local-print/capabilities");
    } catch {
      return null;
    }
  };

/** What `POST /__local-print/order` answers. */
export interface LocalPrintResponse {
  ok?: boolean;
  routed?: number;
  outcome?: string;
  reason?: string;
  message?: string;
  results?: Array<{ ok?: boolean; target?: string; error?: string }>;
}

/**
 * `POST /__local-print/order` — print a receipt on the SHOP'S OWN thermal
 * printer, through `:4001`. PRODUCT CHANGE §3.
 *
 * This is the same endpoint the POS's browser shim posts to
 * (`pos.eposmatic.com/mock-shim.js`), and it exists because **a browser cannot
 * open a raw TCP socket** — there is no way for this page to speak ESC/POS to
 * a printer on port 9100, now or ever. `:4001` can, and does: it resolves the
 * tenant's `printer_settings`, renders the ESC/POS buffer
 * (`mongo-api/src/lib/receiptRender.js`) and writes it to the printer.
 *
 * **`ok: true` does NOT mean anything printed.** A BROADCAST (no `target`)
 * that routes zero jobs answers `200 { ok: true, routed: 0, outcome:
 * "NOTHING_ROUTED", reason }` — deliberately, because a web order matching no
 * POS-only printer is normal traffic and raising it as an error would alarm on
 * a healthy shop. Callers must read `routed`. Measured against the live API:
 * a `WEB_DINE-IN` order answers exactly that, with
 * `reason: "platform WEB_DINE-IN is not in Main Printer's CUSTOMER
 * selectedPlatforms (…)"`.
 *
 * No `target` is sent. Naming one turns a zero-routing answer into a 422, and
 * this app has no business picking which of the shop's printers to use.
 */
export const printOrderOnStorePrinter = async (
  order: Record<string, unknown>,
  requestId: string,
): Promise<LocalPrintResponse> =>
  request<LocalPrintResponse>("/__local-print/order", {
    method: "POST",
    body: JSON.stringify({
      payload: {
        order,
        /**
         * THE UNITS THIS PAYLOAD IS IN, declared rather than assumed.
         *
         * `order` comes from `fetchStoredOrder`, i.e. `GET /orders/:tenantId/:orderId`
         * — the RAW Mongo document, in CENTS. That shape is deliberate (the
         * renderer needs `categories[].items[]`, which the reduced `/web/orders/:id`
         * view does not carry), but the print bridge's DEFAULT is MAJOR units,
         * because the POS hands it an order `getOrderWithPrice` has already
         * converted.
         *
         * Sending cents into a major-unit default is a silent 100× error, and it
         * was live: a Rs 70.60 bill printed as `7060`, every line included.
         * `mongo-api/src/services/localPrint.js` has always supported this flag
         * and says a caller feeding a raw document "must say so"; the route just
         * never forwarded it. It does now, and the conversion is performed by
         * the platform's own `getOrderWithPrice` rather than a second copy of
         * the scaling rules on this side.
         *
         * `tryStorePrinter` confirms the server understands this flag BEFORE it
         * posts — see `fetchLocalPrintCapabilities`.
         */
        units: "cents",
        // The customer's bill only. A KOT is the kitchen's own document and is
        // raised by the POS's own flow; reprinting one from a customer-facing
        // screen would put a second docket on the pass.
        receipt_type: "CUSTOMER",
        // A person pressed Print. This is what lets the server bypass
        // `auto_print_first_order_only`, and it is true.
        manual_print: true,
        request_id: requestId,
      },
    }),
  });

/**
 * `POST /orders/:tenantId/offline-sync` — flush orders taken with no
 * connection. PRODUCT CHANGE §2 (`DEVIATIONS.md`).
 *
 * NOT a `/web/*` route, deliberately, and for the same reason
 * `POST /app-auth/switch/...` is not: this is the platform's OWN sync endpoint,
 * the one the POS's queue already posts to
 * (`pos-recovered/src/features/offlineOrders/offlineOrdersThunks.js`).
 * Duplicating it under `/web/*` would be a second implementation of a contract
 * that already exists, and the two would drift.
 *
 * The BODY IS A BARE ARRAY of complete order documents — the endpoint accepts
 * either that or `{ orders: [...] }`, and the POS sends the array.
 *
 * The RESPONSE IS THE STORED DOCUMENTS, and only the ones that persisted: a
 * partial batch comes back shorter than it went out, with no per-order status
 * envelope. So the only safe reading of "confirmed" is "its `_id` is in the
 * response", which is exactly what `offlineSync.ts` does with it. When NOTHING
 * persisted the endpoint answers 400 with `{ message, failed: [{_id, error}] }`,
 * and `ApiError.body` carries it.
 *
 * It throws. The caller records the failure against the rows and tries again
 * later; a swallowed error here would look like an empty successful batch and
 * the queue would sit there forever.
 */
export const syncOfflineOrders = async (
  tenantId: string,
  documents: unknown[],
): Promise<Array<{ _id?: string }>> => {
  const rows = await request<Array<{ _id?: string }>>(
    `/orders/${encodeURIComponent(tenantId)}/offline-sync`,
    { method: "POST", body: JSON.stringify(documents) },
  );
  return Array.isArray(rows) ? rows : [];
};

export const placeOrder = async (payload: OrderPayload): Promise<Order> => {
  const wire: OrderPayload = { ...payload, items: toWireItems(payload.items) };
  if (USE_MOCK_API) {
    return mockCall(() => mock.createOrder(wire), "/web/orders", {
      method: "POST",
      body: JSON.stringify(wire),
    });
  }
  return request<Order>("/web/orders", {
    method: "POST",
    body: JSON.stringify(wire),
  });
};

/**
 * `POST /customers/send/:tenantId` — the standalone `/customer-form` page's
 * only call. That page is NOT part of checkout: it is a three-step marketing
 * capture (phone → name/email → thank you) with no cart, no guards and no
 * navigation.
 *
 * It THROWS, because the page's own catch is what raises its snackbar. The
 * response body is awaited and never read, exactly as in the original.
 */
export const sendCustomerDetails = async (
  tenantId: string,
  payload: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  },
): Promise<void> => {
  if (USE_MOCK_API || IS_OFFLINE_MODE) return;
  await request<unknown>(`/web/customers/send/${encodeURIComponent(tenantId)}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const submitRating = async (
  token: string,
  payload: RatingPayload,
): Promise<Rating> => {
  if (USE_MOCK_API) {
    return mockCall(
      () => mock.createRating(token, payload),
      `/web/rate/${token}`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );
  }
  return request<Rating>(`/web/rate/${token}`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

/**
 * `GET /deliveries/tracking/:deliveryId` — the tracking page's own fetch (`eT`).
 *
 * The original sends a deterministic platform token with it (`getPublicApiToken`
 * + `x-api-key`) because a tracking link is opened without a customer session;
 * `:4001` scopes the record by the id itself and serves a deliberately narrow
 * projection, so the header pair has no counterpart. Recorded deviation.
 *
 * It THROWS: the page's own catch is what raises `"Delivery not found!"`, which
 * is the only wording that error state has.
 *
 * The `:deliveryId` may be a real `deliveries` document id or the ORDER's id —
 * `:4001` resolves both, and in this deployment it is always the order's,
 * because nothing writes a standalone delivery record.
 */
export const fetchDeliveryTracking = async (
  deliveryId: string,
): Promise<DeliveryTracking> =>
  request<DeliveryTracking>(
    `/deliveries/tracking/${encodeURIComponent(deliveryId)}`,
  );

/**
 * `GET /riders/tracking/:riderId` — the 5s poll's fetch (`eI`).
 *
 * Answers the rider's identity with `lat`/`lng` `null` when no position exists,
 * which is every rider in this deployment. The page renders its "Live tracking
 * not available" panel off exactly that, so a null is data, not a failure.
 */
export const fetchRiderTracking = async (
  riderId: string,
): Promise<RiderLocation> =>
  request<RiderLocation>(`/riders/tracking/${encodeURIComponent(riderId)}`);

export const fetchDelivery = async (
  deliveryId: string,
): Promise<Delivery | null> => {
  if (USE_MOCK_API) {
    return mockCall(
      () => mock.getDelivery(deliveryId),
      `/web/deliveries/${deliveryId}`,
    );
  }
  if (IS_OFFLINE_MODE) return null;
  return request<Delivery | null>(`/web/deliveries/${deliveryId}`);
};
